/**
 * Data Source Parameters
 * Common parameters for components that support S2P files and frequency database
 */

/**
 * Get data source parameters for a component
 * These parameters track which data source is being used for each RF parameter
 */
export const getDataSourceParameters = () => ({
  // Voltage selection for frequency database
  supplyVoltageMode: {
    name: 'Voltage Mode',
    value: 'auto', // 'auto' or 'manual'
    unit: '',
    tracked: true,
    displayOnCard: false,
    description: 'Voltage selection mode (auto-match or manual)'
  },
  selectedVoltage: {
    name: 'Selected Voltage',
    value: null, // Actual voltage being used (auto-matched or manually selected)
    unit: 'V',
    tracked: true,
    displayOnCard: false,
    description: 'Currently selected voltage for frequency data'
  },

  // Data availability flags (set at runtime from component library)
  hasS2PData: {
    name: 'Has S2P Data',
    value: false,
    unit: '',
    tracked: false,
    displayOnCard: false,
    description: 'Component has S2P file available'
  },
  hasFreqData: {
    name: 'Has Freq Data',
    value: false,
    unit: '',
    tracked: false,
    displayOnCard: false,
    description: 'Component has frequency database data'
  },

  // S2P data storage (loaded at runtime)
  s2pData: {
    name: 'S2P Data',
    value: null, // Will contain parsed S2P data: {frequencies: [], s21_mag_db: [], ...}
    unit: '',
    tracked: false,
    displayOnCard: false,
    description: 'Parsed S2P file data'
  },

  // Frequency database storage (loaded at runtime)
  freqDbData: {
    name: 'Freq DB Data',
    value: null, // Will contain freq data: [{frequency_mhz, gain_db, nf_db, ...}, ...]
    unit: '',
    tracked: false,
    displayOnCard: false,
    description: 'Frequency database data for selected voltage'
  }
});

/**
 * Get per-parameter data source tracking
 * Each RF parameter (gain, NF, P1dB, OIP3) can independently select its data source
 */
export const getParameterDataSources = () => ({
  // Gain data source
  gainDataSource: {
    name: 'Gain Source',
    value: 'manual', // 's2p' | 'database' | 'manual'
    unit: '',
    tracked: true,
    displayOnCard: false,
    description: 'Data source for gain parameter'
  },
  gainManualValue: {
    name: 'Gain Manual',
    value: null, // Preserve manual value when switching to other sources
    unit: 'dB',
    tracked: true,
    displayOnCard: false,
    description: 'Manual gain value (preserved when using other sources)'
  },

  // Noise Figure data source
  nfDataSource: {
    name: 'NF Source',
    value: 'manual', // 'database' | 'manual' (S2P doesn't provide NF)
    unit: '',
    tracked: true,
    displayOnCard: false,
    description: 'Data source for noise figure'
  },
  nfManualValue: {
    name: 'NF Manual',
    value: null,
    unit: 'dB',
    tracked: true,
    displayOnCard: false,
    description: 'Manual NF value'
  },

  // OP1dB data source
  p1dbDataSource: {
    name: 'OP1dB Source',
    value: 'manual', // 'database' | 'manual'
    unit: '',
    tracked: true,
    displayOnCard: false,
    description: 'Data source for OP1dB'
  },
  p1dbManualValue: {
    name: 'OP1dB Manual',
    value: null,
    unit: 'dBm',
    tracked: true,
    displayOnCard: false,
    description: 'Manual OP1dB value'
  },

  // OIP3 data source
  oip3DataSource: {
    name: 'OIP3 Source',
    value: 'manual', // 'database' | 'manual'
    unit: '',
    tracked: true,
    displayOnCard: false,
    description: 'Data source for OIP3'
  },
  oip3ManualValue: {
    name: 'OIP3 Manual',
    value: null,
    unit: 'dBm',
    tracked: true,
    displayOnCard: false,
    description: 'Manual OIP3 value'
  }
});

/**
 * Check if a component type supports data sources
 * @param {string} componentType - Component type
 * @returns {boolean} True if component supports S2P/frequency database
 */
export const supportsDataSources = (componentType) => {
  // Components with single input and single output can have S2P/frequency data
  // Measurement component is explicitly excluded
  const supportedTypes = [
    'lna',
    'powerAmplifier',
    'gainBlock',
    'driverAmp',
    'vga',
    'driver',
    'mixer', // Has RF in/out
    'attenuator',
    'variableAttenuator',
    'filter', // All filter types
    'bandpassFilter',
    'lowpassFilter',
    'highpassFilter',
    'bandstopFilter',
    'isolator',
    'circulator', // Can be treated as single path for S2P
    'cable',
    'connector',
    'switch'
  ];

  return supportedTypes.includes(componentType);
};

/**
 * Inject data source parameters into a component definition
 * @param {Object} component - Component definition
 * @returns {Object} Component with data source parameters added
 */
export const injectDataSourceParameters = (component) => {
  if (!component || !component.type) {
    return component;
  }

  // For catalog components, check baseType; for generic components, check type
  const typeToCheck = component.isCatalog ? component.baseType : component.type;

  // Only inject for supported component types
  if (!supportsDataSources(typeToCheck)) {
    return component;
  }

  // Add data source parameters
  const dataSourceParams = getDataSourceParameters();
  const paramDataSources = getParameterDataSources();

  return {
    ...component,
    parameters: {
      ...component.parameters,
      ...dataSourceParams,
      ...paramDataSources
    }
  };
};

/**
 * Initialize data source values when creating a node from component library
 * @param {Object} componentData - Component data from library
 * @param {Object} parameters - Component parameters
 * @param {number} currentFrequency - Current frequency in MHz (optional, for interpolation)
 * @param {Array} freqData - Frequency database data (optional, for interpolation)
 * @returns {Object} Parameters with initialized data source values
 */
export const initializeDataSourceValues = (componentData, parameters, currentFrequency = null, freqData = null) => {
  console.log('initializeDataSourceValues called:', {
    partNumber: componentData.partNumber,
    hasFreqData: componentData.hasFreqData,
    hasS2P: componentData.hasS2P,
    currentFrequency
  });
  const initialized = { ...parameters };

  // Initialize manual value backups with current parameter values
  if (initialized.gain && initialized.gain.value !== undefined) {
    initialized.gainManualValue = {
      ...initialized.gainManualValue,
      value: initialized.gain.value
    };
  }

  if (initialized.noiseFigure && initialized.noiseFigure.value !== undefined) {
    initialized.nfManualValue = {
      ...initialized.nfManualValue,
      value: initialized.noiseFigure.value
    };
  }

  if (initialized.p1db && initialized.p1db.value !== undefined) {
    initialized.p1dbManualValue = {
      ...initialized.p1dbManualValue,
      value: initialized.p1db.value
    };
  }

  if (initialized.oip3 && initialized.oip3.value !== undefined) {
    initialized.oip3ManualValue = {
      ...initialized.oip3ManualValue,
      value: initialized.oip3.value
    };
  }

  // Set data availability flags from component library
  if (componentData.hasS2P) {
    initialized.hasS2PData = {
      ...initialized.hasS2PData,
      value: true
    };
  }

  if (componentData.hasFreqData) {
    initialized.hasFreqData = {
      ...initialized.hasFreqData,
      value: true
    };
  }

  // Initialize selected voltage to component's supply voltage (auto-match mode)
  if (initialized.supplyVoltage && initialized.supplyVoltage.value !== undefined) {
    initialized.selectedVoltage = {
      ...initialized.selectedVoltage,
      value: initialized.supplyVoltage.value
    };
  }

  // Set default data sources based on availability
  // Priority: S2P > Database > Manual

  // Gain: S2P takes priority if available, then Database
  if (componentData.hasS2P && initialized.gainDataSource) {
    initialized.gainDataSource.value = 's2p';
  } else if (componentData.hasFreqData && initialized.gainDataSource) {
    initialized.gainDataSource.value = 'database';
  }

  // NF, P1dB, OIP3: Database if available (S2P doesn't provide these)
  if (componentData.hasFreqData) {
    if (initialized.nfDataSource) {
      initialized.nfDataSource.value = 'database';
    }
    if (initialized.p1dbDataSource) {
      initialized.p1dbDataSource.value = 'database';
    }
    if (initialized.oip3DataSource) {
      initialized.oip3DataSource.value = 'database';
    }
  }

  // IMPORTANT: If frequency data is available and current frequency is known,
  // fetch interpolated values and update parameter display values
  if (componentData.hasFreqData && freqData && Array.isArray(freqData) && freqData.length > 0 && currentFrequency) {
    // Import interpolator (dynamic import to avoid circular dependencies)
    try {
      const { interpolateParameter } = require('../utils/freqDataInterpolator');

      // Interpolate and update each parameter that uses database
      if (initialized.gainDataSource?.value === 'database' && initialized.gain) {
        const result = interpolateParameter(freqData, 'gain_db', currentFrequency);
        if (result && result.value !== null && result.value !== undefined) {
          initialized.gain = {
            ...initialized.gain,
            value: parseFloat(result.value.toFixed(1))
          };
          console.log('Initialized gain from database:', initialized.gain.value);
        }
      }

      if (initialized.nfDataSource?.value === 'database' && initialized.noiseFigure) {
        const result = interpolateParameter(freqData, 'nf_db', currentFrequency);
        if (result && result.value !== null && result.value !== undefined) {
          initialized.noiseFigure = {
            ...initialized.noiseFigure,
            value: parseFloat(result.value.toFixed(1))
          };
          console.log('Initialized NF from database:', initialized.noiseFigure.value);
        }
      }

      if (initialized.p1dbDataSource?.value === 'database' && initialized.p1db) {
        const result = interpolateParameter(freqData, 'p1db_dbm', currentFrequency);
        if (result && result.value !== null && result.value !== undefined) {
          initialized.p1db = {
            ...initialized.p1db,
            value: parseFloat(result.value.toFixed(1))
          };
          console.log('Initialized P1dB from database:', initialized.p1db.value);
        }
      }

      if (initialized.oip3DataSource?.value === 'database' && initialized.oip3) {
        const result = interpolateParameter(freqData, 'oip3_dbm', currentFrequency);
        if (result && result.value !== null && result.value !== undefined) {
          initialized.oip3 = {
            ...initialized.oip3,
            value: parseFloat(result.value.toFixed(1))
          };
          console.log('Initialized OIP3 from database:', initialized.oip3.value);
        }
      }
    } catch (error) {
      console.error('Error interpolating initial database values:', error);
    }
  }

  console.log('initializeDataSourceValues result:', {
    hasFreqData: initialized.hasFreqData?.value,
    hasS2PData: initialized.hasS2PData?.value,
    gainDataSource: initialized.gainDataSource?.value,
    nfDataSource: initialized.nfDataSource?.value,
    gain: initialized.gain?.value,
    noiseFigure: initialized.noiseFigure?.value,
    p1db: initialized.p1db?.value,
    oip3: initialized.oip3?.value
  });

  return initialized;
};

export default {
  getDataSourceParameters,
  getParameterDataSources,
  supportsDataSources,
  injectDataSourceParameters,
  initializeDataSourceValues
};
